import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, FolderOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

interface WiseBoardCase {
  id: string;
  title: string;
  description: string | null;
  board_question: string | null;
  status: string;
  created_at: string;
}

export default function WiseBoardDashboard() {
  const [cases, setCases] = useState<WiseBoardCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [boardQuestion, setBoardQuestion] = useState("");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const fetchCases = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }
    const { data, error } = await supabase
      .from("wiseboard_cases")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setCases(data as WiseBoardCase[]);
    setLoading(false);
  };

  useEffect(() => { fetchCases(); }, []);

  const createCase = async () => {
    if (!title.trim()) return;
    setCreating(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Please sign in first"); setCreating(false); return; }
    
    const { data, error } = await supabase.from("wiseboard_cases").insert({
      user_id: user.id,
      title: title.trim(),
      description: description.trim() || null,
      board_question: boardQuestion.trim() || null,
    }).select().single();

    if (error) { toast.error("Failed to create case"); setCreating(false); return; }
    
    // Create all project entries for this case
    const projectTypes = ['P0','P1','P1B','P2_PASS1','P3','P4','P5','P6','P7','P8','P9','P10','P11','P2_PASS2','P20','P21','P22'];
    await supabase.from("wiseboard_projects").insert(
      projectTypes.map(pt => ({ case_id: data.id, user_id: user.id, project_type: pt }))
    );

    // Create default specialist roster
    const lanes = ['P3','P4','P5','P6','P7','P8','P9','P10','P11'];
    await supabase.from("wiseboard_specialist_roster").insert(
      lanes.map(lane => ({ case_id: data.id, user_id: user.id, lane, selected: true }))
    );

    toast.success("Case created");
    setOpen(false);
    setTitle(""); setDescription(""); setBoardQuestion("");
    setCreating(false);
    navigate(`/wiseboard/case/${data.id}`);
  };

  const deleteCase = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const { error } = await supabase.from("wiseboard_cases").delete().eq("id", id);
    if (!error) { setCases(c => c.filter(x => x.id !== id)); toast.success("Case deleted"); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">WiseBoard</h1>
          <p className="text-muted-foreground mt-1">AI Boardroom — Consulting-Grade Strategy Workflow</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> New Case</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Strategy Case</DialogTitle>
              <DialogDescription>Define the board question and context for this case.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium">Case Title</label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Market Entry — DACH Region" />
              </div>
              <div>
                <label className="text-sm font-medium">Board Question</label>
                <Textarea value={boardQuestion} onChange={e => setBoardQuestion(e.target.value)} placeholder="Should we enter [market/geography]? Assess attractiveness, right-to-win, route-to-market, execution feasibility, and value creation." rows={3} />
              </div>
              <div>
                <label className="text-sm font-medium">Description (optional)</label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Additional context..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createCase} disabled={creating || !title.trim()}>
                {creating ? "Creating..." : "Create Case"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading cases...</div>
      ) : cases.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No cases yet</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Create your first strategy case to start the gated boardroom workflow. Each case follows the full P0→P22 process.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {cases.map(c => (
            <Card key={c.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/wiseboard/case/${c.id}`)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base">{c.title}</CardTitle>
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => deleteCase(c.id, e)}>
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
                <CardDescription className="line-clamp-2">{c.board_question || c.description || "No board question set"}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{c.status}</Badge>
                  <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
